import { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';

const initialQueryString: ReturnType<typeof useSearchParams> = [
  new URLSearchParams(),
  // biome-ignore lint/suspicious/noConsole: reasonable place
  () => console.log('Calling useQueryState outside of context'),
];

const UseQueryState = createContext(initialQueryString);

export const UseQueryStateProvider = ({ children }: { children: React.ReactNode }) => {
  return <UseQueryState.Provider value={useSearchParams()}>{children}</UseQueryState.Provider>;
};

type QueryStringOptions<T> = {
  replace?: boolean;
  toValue?: (value: string) => T;
  valueToString?: (value: T) => string;
};

export const useQueryState = <T,>(
  key: string,
  defaultValue: T,
  options?: QueryStringOptions<T>,
) => {
  const [searchParams, setSearchParams] = useContext(UseQueryState);
  const {
    toValue = (value: string) => value,
    replace = true,
    valueToString = String,
  } = options ?? {};

  const value = useMemo(() => {
    const valueStr = searchParams.get(key);
    return ((valueStr ? toValue(valueStr) : defaultValue) || defaultValue) as T;
  }, [defaultValue, key, searchParams, toValue]);
  const onSetValue = useCallback(
    (newValue: T, options?: { replace?: boolean }) => {
      if (newValue === defaultValue) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, valueToString(newValue));
      }

      setSearchParams(searchParams, { replace: options?.replace ?? replace });
    },
    [defaultValue, setSearchParams, searchParams, replace, valueToString, key],
  );

  return [value, onSetValue] as const;
};
